import { useEffect, useCallback } from 'react';

export interface LightboxCaption {
  title: string;
  location?: string;
  year: string;
}

export interface LightboxImageSize {
  width: number;
  height: number;
}

interface LightboxProps {
  images: readonly string[];
  caption: LightboxCaption;
  captions?: readonly LightboxCaption[];
  imageSizes?: readonly LightboxImageSize[];
  imageClassName?: string;
  currentIndex: number;
  onClose: () => void;
  onNavigate: (index: number) => void;
}

function preloadImage(url: string) {
  const image = new Image();
  image.decoding = 'async';
  image.src = url;
}

export function Lightbox({
  images,
  caption,
  captions,
  imageSizes,
  imageClassName = '',
  currentIndex,
  onClose,
  onNavigate,
}: LightboxProps) {
  const activeCaption = captions?.[currentIndex] ?? caption;
  const activeImageSize = imageSizes?.[currentIndex] ?? {
    width: 3024,
    height: 4032,
  };
  const activeImage = images[currentIndex] ?? images[0] ?? '';

  const handlePrevious = useCallback(() => {
    if (images.length === 0) {
      return;
    }

    const newIndex = currentIndex === 0 ? images.length - 1 : currentIndex - 1;
    onNavigate(newIndex);
  }, [currentIndex, images.length, onNavigate]);

  const handleNext = useCallback(() => {
    if (images.length === 0) {
      return;
    }

    const newIndex = currentIndex === images.length - 1 ? 0 : currentIndex + 1;
    onNavigate(newIndex);
  }, [currentIndex, images.length, onNavigate]);

  useEffect(() => {
    const previousIndex =
      currentIndex === 0 ? images.length - 1 : currentIndex - 1;
    const nextIndex = currentIndex === images.length - 1 ? 0 : currentIndex + 1;
    const nextNextIndex = nextIndex === images.length - 1 ? 0 : nextIndex + 1;

    for (const index of [
      previousIndex,
      currentIndex,
      nextIndex,
      nextNextIndex,
    ]) {
      const image = images[index];

      if (image) {
        preloadImage(image);
      }
    }
  }, [currentIndex, images]);

  useEffect(() => {
    const timers = images.map((image, index) =>
      window.setTimeout(() => preloadImage(image), 1200 + index * 250),
    );

    return () => {
      for (const timer of timers) {
        window.clearTimeout(timer);
      }
    };
  }, [images]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowLeft') {
        handlePrevious();
      } else if (e.key === 'ArrowRight') {
        handleNext();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [onClose, handlePrevious, handleNext]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90">
      <button
        type="button"
        className="absolute inset-0 cursor-default border-0 bg-transparent p-0"
        aria-label="Close lightbox"
        onClick={onClose}
      />

      {/* Close button */}
      <button
        type="button"
        onClick={onClose}
        className="absolute top-6 right-6 text-white/70 hover:text-white transition-colors z-50"
        aria-label="Close lightbox"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="28"
          height="28"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          focusable="false"
        >
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>

      {/* Previous button */}
      {images.length > 1 && (
        <button
          type="button"
          onClick={handlePrevious}
          className="absolute left-6 top-1/2 -translate-y-1/2 text-white/70 hover:text-white transition-colors z-50"
          aria-label="Previous image"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="36"
            height="36"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            focusable="false"
          >
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
      )}

      {/* Image */}
      <button
        type="button"
        className="relative z-10 flex max-h-[90vh] max-w-[90vw] cursor-pointer items-center justify-center border-0 bg-transparent p-0"
        onClick={handleNext}
        aria-label="Show next image"
      >
        <img
          src={activeImage}
          alt={`${activeCaption.title} ${currentIndex + 1} of ${images.length}`}
          width={activeImageSize.width}
          height={activeImageSize.height}
          decoding="async"
          fetchPriority="high"
          className={`max-w-full max-h-[90vh] object-contain ${imageClassName}`}
        />
      </button>

      {/* Next button */}
      {images.length > 1 && (
        <button
          type="button"
          onClick={handleNext}
          className="absolute right-6 top-1/2 -translate-y-1/2 text-white/70 hover:text-white transition-colors z-50"
          aria-label="Next image"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="36"
            height="36"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            focusable="false"
          >
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </button>
      )}

      {/* Image counter */}
      {images.length > 1 && (
        <div className="absolute bottom-6 right-6 text-right text-sm text-white/70">
          {currentIndex + 1} / {images.length}
        </div>
      )}

      <div className="absolute bottom-6 left-6 text-white text-sm leading-relaxed font-light">
        <p>
          <em>{activeCaption.title}</em>, {activeCaption.year}
        </p>
        {activeCaption.location && <p>{activeCaption.location}</p>}
      </div>
    </div>
  );
}
